<template>
  <div class="time-range-picker">
    <el-dropdown
            ref="dropdown"
            trigger="click"
            placement="bottom-start"
            :hide-on-click="false"
            @command="handleCommand"
            @visible-change="handleVisibleChange"
    >
      <span class="el-dropdown-link">
        <el-input
                v-model="timeRangeText"
                placeholder="请选择时间段"
                readonly
                :style="{ width: width }"
                class="time-range-input"
        >
          <i
                  slot="suffix"
                  class="el-icon-arrow-down el-icon--right"
                  :class="{ 'is-reverse': dropdownVisible }"
          ></i>
        </el-input>
      </span>

      <el-dropdown-menu
              slot="dropdown"
              class="time-range-dropdown"
      >
        <!-- 快捷选项 -->
        <el-dropdown-item
                v-for="item in shortcutOptions"
                :key="item.command"
                :command="item.command"
                :class="{ 'active-shortcut': activeShortcut === item.command }"
        >
          {{ item.label }}
        </el-dropdown-item>

        <el-dropdown-item divided class="custom-range-item">
          <div class="custom-range-container">
            <div class="custom-range-title">自定义范围</div>
            <div class="date-pickers-container">
              <div class="date-picker-group">
                <label>开始时间</label>
                <el-date-picker
                        ref="startDatePicker"
                        v-model="customStartDate"
                        type="date"
                        placeholder="选择开始日期"
                        :picker-options="startPickerOptions"
                        value-format="yyyy-MM-dd"
                        @change="handleStartDateChange"
                        :clearable="false"
                />
              </div>
              <div class="date-picker-group">
                <label>结束时间</label>
                <el-date-picker
                        ref="endDatePicker"
                        v-model="customEndDate"
                        type="date"
                        placeholder="选择结束日期"
                        :picker-options="endPickerOptions"
                        value-format="yyyy-MM-dd"
                        @change="handleEndDateChange"
                        :clearable="false"
                />
              </div>
            </div>
            <div class="dropdown-actions">
              <el-button size="mini" @click="handleReset">重置</el-button>
              <el-button
                      type="primary"
                      size="mini"
                      :disabled="!isCustomDateValid"
                      @click="handleConfirmCustom"
              >
                确定
              </el-button>
            </div>
          </div>
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
  </div>
</template>

<script>
  export default {
    name: 'TimeRangePicker',
    props: {
      // 绑定值，格式为 [startDate, endDate]
      value: {
        type: Array,
        default: () => []
      },
      // 输入框宽度
      width: {
        type: String,
        default: '250px'
      },
      // 日期格式
      format: {
        type: String,
        default: 'yyyy-MM-dd'
      },
      // 选择日期后是否自动关闭下拉框
      autoCloseOnDateSelect: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        // 当前选中的快捷选项
        activeShortcut: '',
        // 自定义开始时间
        customStartDate: '',
        // 自定义结束时间
        customEndDate: '',
        // 下拉框显示状态
        dropdownVisible: false,
        // 快捷选项配置
        shortcutOptions: [
          { label: '今天', command: 'today' },
          { label: '昨天', command: 'yesterday' },
          { label: '最近7天', command: 'last7days' },
          { label: '最近30天', command: 'last30days' },
          { label: '本月', command: 'thisMonth' },
          { label: '上月', command: 'lastMonth' }
        ],
        // 记录日期选择状态，避免重复触发
        dateSelectionInProgress: false
      };
    },
    computed: {
      // 显示在输入框中的文本
      timeRangeText() {
        if (this.value && this.value.length === 2) {
          return `${this.value[0]} 至 ${this.value[1]}`;
        }
        return '';
      },

      // 自定义日期是否有效
      isCustomDateValid() {
        return this.customStartDate && this.customEndDate;
      },

      // 开始日期选择器的配置（限制不能晚于结束日期）
      startPickerOptions() {
        return {
          disabledDate: (time) => {
            if (this.customEndDate) {
              return time.getTime() > new Date(this.customEndDate).getTime();
            }
            return false;
          }
        };
      },

      // 结束日期选择器的配置（限制不能早于开始日期）
      endPickerOptions() {
        return {
          disabledDate: (time) => {
            if (this.customStartDate) {
              return time.getTime() < new Date(this.customStartDate).getTime();
            }
            return false;
          }
        };
      }
    },
    watch: {
      // 监听value变化，更新自定义日期
      value: {
        handler(newVal) {
          if (newVal && newVal.length === 2) {
            this.customStartDate = newVal[0];
            this.customEndDate = newVal[1];
          }
        },
        immediate: true
      }
    },
    methods: {
      /**
       * 处理下拉框显示状态变化
       */
      handleVisibleChange(visible) {
        this.dropdownVisible = visible;
        if (!visible) {
          // 下拉框关闭时重置选择状态
          this.dateSelectionInProgress = false;
        }
      },

      /**
       * 处理快捷选项选择
       */
      handleCommand(command) {
        this.activeShortcut = command;
        const range = this.calculateDateRange(command);
        if (range) {
          this.$emit('input', [range.start, range.end]);
          this.$emit('change', [range.start, range.end]);
          // 选择快捷选项后立即关闭下拉框
          this.closeDropdown();
        }
      },

      /**
       * 计算日期范围
       */
      calculateDateRange(type) {
        const now = new Date();
        let start = new Date();
        let end = new Date();

        switch (type) {
          case 'today':
            start = new Date(now);
            end = new Date(now);
            break;

          case 'yesterday':
            start = new Date(now);
            end = new Date(now);
            start.setDate(now.getDate() - 1);
            end.setDate(now.getDate() - 1);
            break;

          case 'last7days':
            start = new Date(now);
            start.setDate(now.getDate() - 6);
            end = new Date(now);
            break;

          case 'last30days':
            start = new Date(now);
            start.setDate(now.getDate() - 29);
            end = new Date(now);
            break;

          case 'thisMonth':
            start = new Date(now.getFullYear(), now.getMonth(), 1);
            end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            break;

          case 'lastMonth':
            start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            end = new Date(now.getFullYear(), now.getMonth(), 0);
            break;

          default:
            return null;
        }

        return {
          start: this.formatDate(start),
          end: this.formatDate(end)
        };
      },

      /**
       * 格式化日期
       */
      formatDate(date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      },

      /**
       * 处理开始日期变化
       */
      handleStartDateChange() {
        this.handleDateChange('start');
      },

      /**
       * 处理结束日期变化
       */
      handleEndDateChange() {
        this.handleDateChange('end');
      },

      /**
       * 处理日期变化通用逻辑
       */
      handleDateChange(type) {
        console.log(type)
        if (this.dateSelectionInProgress) {
          return;
        }

        this.dateSelectionInProgress = true;

        // 延迟处理，确保两个日期选择器都更新完成
        this.$nextTick(() => {
          // 如果两个日期都已选择
          if (this.customStartDate && this.customEndDate) {
            this.activeShortcut = 'custom';

            // 如果开启了选择日期后自动关闭，且两个日期都有效
            if (this.autoCloseOnDateSelect && this.isCustomDateValid) {
              // 延迟关闭，让用户看到选择结果
              setTimeout(() => {
                this.$emit('input', [this.customStartDate, this.customEndDate]);
                this.$emit('change', [this.customStartDate, this.customEndDate]);
                this.closeDropdown();
                this.dateSelectionInProgress = false;
              }, 300);
            } else {
              this.$emit('input', [this.customStartDate, this.customEndDate]);
              this.$emit('change', [this.customStartDate, this.customEndDate]);
              this.dateSelectionInProgress = false;
            }
          } else {
            this.dateSelectionInProgress = false;
          }
        });
      },

      /**
       * 确认自定义日期范围
       */
      handleConfirmCustom() {
        if (this.isCustomDateValid) {
          this.activeShortcut = 'custom';
          this.$emit('input', [this.customStartDate, this.customEndDate]);
          this.$emit('change', [this.customStartDate, this.customEndDate]);
          this.closeDropdown();
        }
      },

      /**
       * 重置选择
       */
      handleReset() {
        this.customStartDate = '';
        this.customEndDate = '';
        this.activeShortcut = '';
        this.$emit('input', []);
        this.$emit('change', []);
        // 重置后不关闭下拉框，让用户可以继续选择
      },

      /**
       * 关闭下拉框
       */
      closeDropdown() {
        if (this.$refs.dropdown) {
          this.$refs.dropdown.hide();
        }
      },

      /**
       * 外部调用的方法：获取当前值
       */
      getValue() {
        return this.value || [];
      },

      /**
       * 外部调用的方法：设置值
       */
      setValue(startDate, endDate) {
        this.$emit('input', [startDate, endDate]);
        this.$emit('change', [startDate, endDate]);
      },

      /**
       * 外部调用的方法：打开下拉框
       */
      openDropdown() {
        if (this.$refs.dropdown) {
          this.$refs.dropdown.show();
        }
      },

      /**
       * 外部调用的方法：关闭下拉框
       */
      hideDropdown() {
        this.closeDropdown();
      }
    }
  };
</script>

<style scoped>
  .time-range-picker {
    display: inline-block;
  }

  .time-range-input {
    cursor: pointer;
  }

  .time-range-dropdown {
    width: 320px;
    padding: 0;
  }

  .custom-range-item {
    padding: 0 !important;
  }

  .custom-range-container {
    padding: 12px;
  }

  .custom-range-title {
    font-weight: bold;
    margin-bottom: 12px;
    color: #333;
  }

  .date-pickers-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
  }

  .date-picker-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .date-picker-group label {
    font-size: 12px;
    color: #666;
  }

  .dropdown-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .active-shortcut {
    color: #409eff;
    background-color: #f0f7ff;
  }

  /* 下拉箭头旋转效果 */
  .el-icon-arrow-down {
    transition: transform 0.3s;
  }

  .el-icon-arrow-down.is-reverse {
    transform: rotate(180deg);
  }
</style>
